import React, { useEffect, useMemo } from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';

// icons
import { CloudDownload, LockKeyhole, LockKeyholeOpen } from 'lucide-react';

// types
import { Event, EventType, EventTarget, GatewaySettingsType } from '../../utils/events';

// functions
import { displayFileSize } from '../../utils/functions';

// classes
import ToolButton from '../../tools/tool-button';
import PleaseWait from '../../tools/please-wait';
import MiniButton from '../../tools/mini-button';

// types
import { ToolButtonType } from '../../tools/tool-button';
import { DataItemType, RucioLocationsType } from '../../utils/types';
import { handleEvent } from '../../app-actions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../../app/store';

import { SettingsState } from '../../app/settings-slice';
import { setUserDropdownMenuDisplayed } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function RucioFilesTable() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.project.loadingRucioFiles,
  );

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );

  // global slice.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  const sErrorMessage: string = useAppSelector((state) => state.project.errorMessage);

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <>
      {sG_loggedIn !== true && (
        <div className="no-data-overlay">
          <span>Login required</span>
        </div>
      )}
      {sG_loggedIn === true && sG_project === undefined && (
        <div className="no-data-overlay">
          <span>No project selected</span>
        </div>
      )}

      {sLoadingComponent === true && (
        <div style={{ flex: '0 0 auto', margin: '10px 0px 10px 10px', width: '90%' }}>
          <PleaseWait text={t('Loading, please wait') + '.....'} visible={true} />
        </div>
      )}

      {/* display the list of data items */}
      <DataItems key={sProjectItems.length.toString()} errorMessage={sErrorMessage} />
    </>
  );
  //
} // RucioFilesTable

//  --------------------------------------------------------------------------
//
//  S U B   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Display a small sized icon for each item, with a single
//  row of details.
//
//  ------------------------------------------------------------

type AccessState = 'accessible' | 'inaccessible' | 'checking';

function AccessLockIcon(props: { accessState: AccessState }) {
  //
  const { t } = useTranslation();

  if (props.accessState === 'accessible') {
    return (
      <LockKeyholeOpen
        className="text-colour"
        data-text-green="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  if (props.accessState === 'inaccessible') {
    return (
      <LockKeyhole
        className="text-colour"
        data-text-red="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  return (
    <i
      className="pi pi-spin pi-spinner srctx-info"
      style={{
        fontSize: '1.8rem',
      }}
      title={t('Checking access to namespace')}
    />
  );
  //
} // AccessLockIcon

//  ------------------------------------------------------------
//
//  Display an icon and name for each data item.
//
//  ------------------------------------------------------------

function DataItems(props: { errorMessage: string }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );
  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.project.loadingRucioFiles,
  );

  // global slice.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );

  // namespace-access lookup.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // build a single keyed lookup of namespace access so each row is an O(1)
  // resolution rather than walking sD_rucioLocations per render.
  const accessByKey: Map<string, boolean | undefined> = useMemo(() => {
    const map = new Map<string, boolean | undefined>();
    for (const r of sD_rucioLocations) {
      map.set(`${r.namespace}:${r.filename}`, r.userCanAccessNamespace);
    }
    return map;
  }, [sD_rucioLocations]);

  const resolveAccessState = (
    item: DataItemType,
  ): 'accessible' | 'inaccessible' | 'checking' => {
    const value = accessByKey.get(`${item.namespace}:${item.filename}`);
    if (value === true) return 'accessible';
    if (value === false) return 'inaccessible';
    return 'checking';
  };

  //  ------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  ------------------------------------------------------------

  const hasNoItems = Object.keys(sProjectItems).length === 0;

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  if (hasNoItems === true && sLoadingComponent === false && sG_project !== undefined) {
    return (
      <div
        className="text-colour"
        data-muted="T"
        style={{
          flex: '0 0 60px',
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'center',
          margin: '0px 0px 0px 10px',
        }}
      >
        No data items found
      </div>
    );
  }

  return (
    <>
      {sProjectItems.map((item: DataItemType, index: number) => {
        const accessState = resolveAccessState(item);
        const showLockIcon = sG_loggedIn === true;
        return (
          <div
            style={{
              flex: '0 0 60px',
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              margin: '0px 0px 0px 0px',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: '0 0 10px' }} />
            <MiniButton
              className="text-colour"
              data-mini-button="T"
              title="Download file"
            >
              <CloudDownload width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
            <div style={{ flex: '0 0 35px' }} />
            <div
              style={{
                flex: '1 1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  flex: '0 0 auto',
                }}
              >
                {item.target_name ?? 'Unknown source'}
              </div>
              <div
                className="text-colour"
                style={{
                  whiteSpace: 'nowrap',
                  flex: '0 0 auto',
                  alignItems: 'center',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                [{item.namespace + ':' + item.filename}]
              </div>
            </div>
            <div style={{ flex: '0 0 10px' }} />
            <div
              className="text-colour"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 300px',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {(item.did_type ?? '').toUpperCase()}
            </div>
            <div style={{ flex: '0 0 20px' }} />
            <div
              className="text-colour"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 200px',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              size unavailable{/*displayFileSize({ bytes: item.bytes ?? 0 })*/}
            </div>
            {showLockIcon && <AccessLockIcon accessState={accessState} />}
            <div style={{ flex: '0 0 20px' }} />
          </div>
        );
      })}
    </>
  );
} // DataItems
